/**
 * Upgrade Medic Data Service
 *
 * Fetches Lambda function inventory from DynamoDB and transforms it
 * into the UI-compatible Account/Pipeline/LambdaFunction structure.
 *
 * NOTE: Email notifications are now handled by the backend WebSocket server.
 * The server sends emails on job_completed, job_failed, and job_cancelled events.
 */

import { DynamoDBService } from 'src/services/aws/DynamoDBService';
import { getUpgradeMedicConfig } from 'src/constants/EnvironmentConstants';
import { getStageConfig } from 'src/components/fintech-ops/upgrade-medic/config';
import { logger } from 'src/logger';
import {
  Account,
  LambdaFunction,
  UpgradeMedicResource,
  EmrResource,
  Pipeline,
  JobRun,
  JobStage,
  StageInfo,
  JobArtifacts,
  createInitialStages,
  VersionSetDetail,
  BrazilEOLInterpreterData,
  AccountDetailsMap
} from 'src/components/fintech-ops/upgrade-medic/types';

// DynamoDB item structure from upgrade-medic resource inventory table
interface UpgradeMedicResourceItem {
  resourceType: string; // PK: "lambda"
  resourceArn: string; // SK: Full Lambda ARN
  accountName: string; // e.g., "DaSOE-Prod"
  region: string; // e.g., "us-west-2"
  resourceName: string; // Function name
  currentVersion: string; // Runtime e.g., "python3.11"
  versionDetails?: {
    // Optional runtime details
    runtime?: string;
  };
  pipelineId?: string; // Pipeline/VersionSet name
  cfnStackArn?: string; // CloudFormation stack ARN
  activeJobId?: string; // "NONE" or job ID
  lastScanDate?: string; // ISO timestamp
  emrType?: string; // EMR resources only: "cluster" | "serverless"
}

// DynamoDB item structure from upgrade-medic versionset details table
interface VersionSetDynamoItem {
  Pipeline: string;
  VersionSet: string;
  CPython3_Runtime: string;
  NodeJS_Runtime: string;
}

// Key parts of the auto-scheduler's USAGE_ASSESSMENT items on the v2 job-runs table. Must match
// DaSFintech-ResourceScannerService scheduler_state.py (SCHEDULER_STATE_KEY_PREFIX / item type).
const SCHEDULER_STATE_KEY_PREFIX = 'auto-scheduler';
const USAGE_ASSESSMENT_ITEM_TYPE = 'USAGE_ASSESSMENT';

/** The auto-scheduler's usage verdict for one manually-managed resource, shown in the ack modal. */
export interface ResourceAssessment {
  /** 'delete-candidate' (idle) or 'should-update' (in use); '' when the item omitted it. */
  recommendation: string;
  /** Human-readable evidence lines behind the verdict (log/CloudTrail/stack/creation signals). */
  evidence: string[];
  inUse: boolean;
  targetVersion?: string;
}

// EOL version regex patterns for Brazil interpreters - versions that are End of Life
// Format examples: CPython310-default, CPython38-default, NodeJS-16.x, NodeJS-18.x
const CPYTHON_EOL_PATTERNS = [
  /cpython38/i, // Matches CPython38-default
  /cpython39/i, // Matches CPython39-default
  /cpython3[._]?8/i,
  /cpython3[._]?9/i,
  /python38/i,
  /python39/i
];

const NODEJS_EOL_PATTERNS = [
  /nodejs-16/i, // Matches NodeJS-16.x
  /nodejs-18/i, // Matches NodeJS-18.x
  /nodejs16/i,
  /nodejs18/i,
  /node.*-16/i,
  /node.*-18/i
];

// Current/Recommended version patterns
const CPYTHON_CURRENT_PATTERNS = [
  /cpython311/i, // Matches CPython311-default
  /cpython312/i, // Matches CPython312-default
  /cpython310/i, // Matches CPython310-default
  /cpython3[._]?11/i,
  /cpython3[._]?12/i,
  /cpython3[._]?10/i
];

const NODEJS_CURRENT_PATTERNS = [
  /nodejs-20/i, // Matches NodeJS-20.x
  /nodejs-22/i, // Matches NodeJS-22.x
  /nodejs20/i,
  /nodejs22/i,
  /node.*-20/i,
  /node.*-22/i
];

// Helper functions to check if a version is EOL
const isCPythonEOL = (version: string): boolean => {
  if (!version) return false;
  return CPYTHON_EOL_PATTERNS.some((pattern) => pattern.test(version));
};

const isNodeJSEOL = (version: string): boolean => {
  if (!version) return false;
  return NODEJS_EOL_PATTERNS.some((pattern) => pattern.test(version));
};

const isCPythonCurrent = (version: string): boolean => {
  if (!version) return false;
  return CPYTHON_CURRENT_PATTERNS.some((pattern) => pattern.test(version));
};

const isNodeJSCurrent = (version: string): boolean => {
  if (!version) return false;
  return NODEJS_CURRENT_PATTERNS.some((pattern) => pattern.test(version));
};

export class UpgradeMedicDataService {
  /**
   * Fetch Lambda inventory from DynamoDB and transform to UI structure
   * Groups resources by Account -> Pipeline -> Lambda
   */
  public static async getLambdaInventory(): Promise<Account[]> {
    const config = getUpgradeMedicConfig();

    try {
      console.log('[UpgradeMedicDataService] Fetching from:', config.tableName);

      // Fetch all items from cross-account DynamoDB
      const items = await DynamoDBService.getAllItemsCrossAccount(config.tableName, config.roleArn, config.region);

      console.log(`[UpgradeMedicDataService] Fetched ${items.length} items`);

      // Filter for Lambda resources only
      const lambdaItems = items.filter((item: UpgradeMedicResourceItem) => item.resourceType === 'lambda');

      console.log(`[UpgradeMedicDataService] ${lambdaItems.length} Lambda resources`);

      // Transform to UI structure
      return this.transformToAccountStructure(lambdaItems);
    } catch (error) {
      console.error('[UpgradeMedicDataService] Error fetching inventory:', error);
      throw error;
    }
  }

  /**
   * Fetch EMR inventory from DynamoDB and transform to UI structure.
   * Groups resources by Account -> Pipeline -> Cluster (reuses Account/Pipeline/LambdaFunction types).
   */
  public static async getEmrInventory(): Promise<Account[]> {
    const config = getUpgradeMedicConfig();

    try {
      console.log('[UpgradeMedicDataService] Fetching EMR inventory from:', config.tableName);

      const items = await DynamoDBService.getAllItemsCrossAccount(config.tableName, config.roleArn, config.region);

      const emrItems = items.filter((item: UpgradeMedicResourceItem) => item.resourceType === 'emr');

      console.log(`[UpgradeMedicDataService] ${emrItems.length} EMR resources`);

      return this.transformToAccountStructure(emrItems);
    } catch (error) {
      console.error('[UpgradeMedicDataService] Error fetching EMR inventory:', error);
      throw error;
    }
  }

  /**
   * Fetch VersionSet details from DynamoDB for Brazil EOL Interpreter VS card
   * Returns data grouped by CPython and NodeJS runtime versions
   */
  public static async getVersionSetDetails(): Promise<BrazilEOLInterpreterData> {
    const config = getUpgradeMedicConfig();
    const stageConfig = getStageConfig();
    const tableName = stageConfig.tables.versionSetDetails;

    try {
      console.log('[UpgradeMedicDataService] Fetching versionset details from:', tableName);

      // Fetch all items from the versionset-details table
      const items = await DynamoDBService.getAllItemsCrossAccount(tableName, config.roleArn, config.region);

      console.log(`[UpgradeMedicDataService] Fetched ${items.length} versionset items`);

      // Transform to structured data
      return this.transformVersionSetData(items as VersionSetDynamoItem[]);
    } catch (error) {
      console.error('[UpgradeMedicDataService] Error fetching versionset details:', error);
      throw error;
    }
  }

  /**
   * Fetch account details (manager, owner, team) from the aws-account-details-cdk table in us-east-1.
   * Returns a Map keyed by accountName for fast lookup.
   */
  public static async getAccountDetails(): Promise<AccountDetailsMap> {
    const config = getUpgradeMedicConfig();
    const tableName = 'aws-account-details-cdk';

    try {
      logger.info(`[UpgradeMedicDataService] Fetching account details from: ${tableName}`);
      const items = await DynamoDBService.getAllItemsCrossAccount(tableName, config.roleArn, 'us-east-1');
      logger.info(`[UpgradeMedicDataService] Fetched ${items.length} account detail records`);

      const detailsMap: AccountDetailsMap = new Map();
      items.forEach((item: Record<string, any>) => {
        const accountName = item.account_name || '';
        const accountId = item.accountId || item.account_id || '';
        if (!accountName && !accountId) return;

        const manager = item.owning_manager_display_name || '';
        const owner = item.team_poc_display_name || '';
        const team = item.owning_team || '';
        const leadershipHierarchy = item.leadership_hierarchy || '';
        const stage = item.stage || '';
        const record = { accountId, accountName, manager, owner, team, leadershipHierarchy, stage };

        if (accountName) detailsMap.set(accountName, record);
        if (accountId) detailsMap.set(accountId, record);
      });

      logger.info(`[UpgradeMedicDataService] Account details map size: ${detailsMap.size}`);
      return detailsMap;
    } catch (error) {
      logger.error('[UpgradeMedicDataService] Error fetching account details:', error as Error);
      throw error;
    }
  }

  /**
   * Transform raw versionset items into grouped structure for Brazil EOL Interpreter card.
   * Shows ALL versionsets grouped by interpreter version (not just EOL).
   */
  private static transformVersionSetData(items: VersionSetDynamoItem[]): BrazilEOLInterpreterData {
    // Track EOL counts
    let cpythonEOLCount = 0;
    let nodejsEOLCount = 0;

    // Track unique EOL versions found
    const foundCpythonEOLVersions = new Set<string>();
    const foundNodejsEOLVersions = new Set<string>();

    // Group by CPython runtime version (ALL versions, not just EOL)
    const cpythonByVersion = new Map<string, Map<string, VersionSetDetail[]>>();
    // Group by NodeJS runtime version (ALL versions, not just EOL)
    const nodejsByVersion = new Map<string, Map<string, VersionSetDetail[]>>();

    items.forEach((item) => {
      const versionSetDetail: VersionSetDetail = {
        pipeline: item.Pipeline,
        versionSet: item.VersionSet,
        cpythonRuntime: item.CPython3_Runtime,
        nodejsRuntime: item.NodeJS_Runtime
      };

      // Group ALL CPython versions
      const cpythonVersion = item.CPython3_Runtime;
      if (cpythonVersion) {
        // Track EOL count
        if (isCPythonEOL(cpythonVersion)) {
          cpythonEOLCount++;
          foundCpythonEOLVersions.add(cpythonVersion);
        }

        // Group by version -> pipeline -> versionsets
        if (!cpythonByVersion.has(cpythonVersion)) {
          cpythonByVersion.set(cpythonVersion, new Map());
        }
        const pipelineMap = cpythonByVersion.get(cpythonVersion)!;
        if (!pipelineMap.has(item.Pipeline)) {
          pipelineMap.set(item.Pipeline, []);
        }
        pipelineMap.get(item.Pipeline)!.push(versionSetDetail);
      }

      // Group ALL NodeJS versions
      const nodejsVersion = item.NodeJS_Runtime;
      if (nodejsVersion) {
        // Track EOL count
        if (isNodeJSEOL(nodejsVersion)) {
          nodejsEOLCount++;
          foundNodejsEOLVersions.add(nodejsVersion);
        }

        // Group by version -> pipeline -> versionsets
        if (!nodejsByVersion.has(nodejsVersion)) {
          nodejsByVersion.set(nodejsVersion, new Map());
        }
        const pipelineMap = nodejsByVersion.get(nodejsVersion)!;
        if (!pipelineMap.has(item.Pipeline)) {
          pipelineMap.set(item.Pipeline, []);
        }
        pipelineMap.get(item.Pipeline)!.push(versionSetDetail);
      }
    });

    // Convert maps to grouped arrays - sort EOL versions first, then by count
    const cpythonGroups = Array.from(cpythonByVersion.entries())
      .map(([version, pipelineMap]) => ({
        runtime: version,
        isEOL: isCPythonEOL(version),
        pipelines: Array.from(pipelineMap.entries())
          .map(([pipelineName, versionsets]) => ({
            name: pipelineName,
            versionsets
          }))
          .sort((a, b) => a.name.localeCompare(b.name)),
        totalCount: Array.from(pipelineMap.values()).reduce((sum, vs) => sum + vs.length, 0)
      }))
      // Sort: EOL first, then by count descending
      .sort((a, b) => {
        if (a.isEOL && !b.isEOL) return -1;
        if (!a.isEOL && b.isEOL) return 1;
        return b.totalCount - a.totalCount;
      });

    const nodejsGroups = Array.from(nodejsByVersion.entries())
      .map(([version, pipelineMap]) => ({
        runtime: version,
        isEOL: isNodeJSEOL(version),
        pipelines: Array.from(pipelineMap.entries())
          .map(([pipelineName, versionsets]) => ({
            name: pipelineName,
            versionsets
          }))
          .sort((a, b) => a.name.localeCompare(b.name)),
        totalCount: Array.from(pipelineMap.values()).reduce((sum, vs) => sum + vs.length, 0)
      }))
      // Sort: EOL first, then by count descending
      .sort((a, b) => {
        if (a.isEOL && !b.isEOL) return -1;
        if (!a.isEOL && b.isEOL) return 1;
        return b.totalCount - a.totalCount;
      });

    // Calculate total counts for each runtime type
    const cpythonTotalCount = cpythonGroups.reduce((sum, g) => sum + g.totalCount, 0);
    const nodejsTotalCount = nodejsGroups.reduce((sum, g) => sum + g.totalCount, 0);

    return {
      totalVersionsets: items.length,
      cpythonEOLCount,
      nodejsEOLCount,
      cpythonTotalCount,
      nodejsTotalCount,
      cpythonGroups,
      nodejsGroups,
      eolVersions: {
        cpython: Array.from(foundCpythonEOLVersions),
        nodejs: Array.from(foundNodejsEOLVersions)
      },
      currentVersions: {
        cpython: ['CPython311', 'CPython312', 'CPython313'],
        nodejs: ['NodeJS-20.x', 'NodeJS-22.x']
      }
    };
  }

  /**
   * Extract CloudFormation stack name from ARN
   * e.g., arn:aws:cloudformation:us-east-1:021891595497:stack/LabFinYetiService-vpcStack-gamma/8cf91af0-91d2-11ef-b119-0affc02ec4e3
   * returns: LabFinYetiService-vpcStack-gamma
   */
  private static extractStackNameFromArn(cfnStackArn?: string): string | null {
    if (!cfnStackArn) return null;

    // ARN format: arn:aws:cloudformation:{region}:{account}:stack/{stack-name}/{id}
    const parts = cfnStackArn.split('/');
    if (parts.length >= 2) {
      return parts[1]; // Stack name is the second part after splitting by '/'
    }
    return null;
  }

  /**
   * Determine pipeline name for a resource
   * - If pipelineId exists and is not manual, use it
   * - Manual deployments WITH cfnStackArn go to "Manual Deployed Stacks" pipeline
   * - Manual deployments WITHOUT cfnStackArn go to "Manually created" pipeline
   */
  private static determinePipelineName(item: UpgradeMedicResourceItem): { name: string; isManual: boolean; stackName?: string } {
    const pipelineId = item.pipelineId;

    // Regular pipelines - not manual
    if (pipelineId && pipelineId !== 'Manually created' && pipelineId !== 'Manual Stack deployment' && pipelineId !== 'local_development') {
      return { name: pipelineId, isManual: false };
    }

    // Check if there's a CloudFormation stack ARN
    const stackName = this.extractStackNameFromArn(item.cfnStackArn);

    // If has CFN stack -> "Manual Deployed Stacks" with sub-grouping
    if (stackName) {
      return {
        name: 'Manual Deployed Stacks',
        isManual: true,
        stackName: stackName
      };
    }

    // No CFN stack -> separate "Manually created" pipeline
    return {
      name: 'Manually created',
      isManual: true
    };
  }

  /**
   * Transform flat DynamoDB items into hierarchical Account -> Pipeline -> Lambda structure
   */
  private static transformToAccountStructure(items: UpgradeMedicResourceItem[]): Account[] {
    // Group by account name
    const accountMap = new Map<string, Map<string, { lambdas: LambdaFunction[]; isManual: boolean }>>();

    items.forEach((item) => {
      const accountName = item.accountName || 'Unknown Account';
      const pipelineInfo = this.determinePipelineName(item);
      const pipelineName = pipelineInfo.name;
      const isManual = pipelineInfo.isManual;
      const stackName = pipelineInfo.stackName;

      // Get or create account entry
      if (!accountMap.has(accountName)) {
        accountMap.set(accountName, new Map());
      }
      const pipelineMap = accountMap.get(accountName)!;

      // Get or create pipeline entry
      if (!pipelineMap.has(pipelineName)) {
        pipelineMap.set(pipelineName, { lambdas: [], isManual });
      }

      // The DynamoDB emrType discriminator ("cluster" | "serverless") drives the display type
      // shown in the Type column. Keep the discriminator itself on the model too — downstream
      // consumers (job update-type routing) must read it rather than re-parse the display label.
      const emrType = item.emrType === 'serverless' || item.emrType === 'cluster' ? item.emrType : undefined;

      const base = {
        id: item.resourceArn,
        name: item.resourceName,
        arn: item.resourceArn,
        runtime: item.currentVersion || item.versionDetails?.runtime || 'unknown',
        status: this.mapJobStatus(item.activeJobId),
        region: item.region,
        stackName: isManual ? stackName : undefined
      };

      const resource: UpgradeMedicResource = emrType
        ? ({ ...base, type: emrType === 'serverless' ? 'Serverless' : 'Cluster', emrType } as EmrResource)
        : { ...base, type: 'Lambda' };

      pipelineMap.get(pipelineName)!.lambdas.push(resource);
    });

    // Convert maps to Account array
    const accounts: Account[] = [];
    let accountIndex = 0;

    accountMap.forEach((pipelineMap, accountName) => {
      accountIndex++;
      const pipelines: Pipeline[] = [];
      let pipelineIndex = 0;

      pipelineMap.forEach(({ lambdas, isManual }, pipelineName) => {
        pipelineIndex++;
        pipelines.push({
          id: `pipe-${accountIndex}-${pipelineIndex}`,
          name: pipelineName,
          lambdas: lambdas,
          isManual
        });
      });

      // Sort pipelines alphabetically, but keep manual stacks at the end
      pipelines.sort((a, b) => {
        if (a.isManual && !b.isManual) return 1;
        if (!a.isManual && b.isManual) return -1;
        return a.name.localeCompare(b.name);
      });

      accounts.push({
        id: `account-${accountIndex}`,
        name: accountName,
        pipelines
      });
    });

    // Sort accounts alphabetically
    accounts.sort((a, b) => a.name.localeCompare(b.name));

    return accounts;
  }

  /**
   * Map activeJobId to status
   * - "NONE" or undefined = Done
   * - Any other value = Running
   */
  private static mapJobStatus(activeJobId?: string): LambdaFunction['status'] {
    if (!activeJobId || activeJobId === 'NONE') {
      return 'Done';
    }
    return 'Running';
  }

  // ==========================================
  // JOB RUN CRUD OPERATIONS
  // ==========================================

  /**
   * Map backend resource_type variants to the canonical frontend value.
   * Backend may store 'manual_lambda', 'lambda', 'Lambda', etc.
   */
  private static normalizeResourceType(raw?: string): string {
    if (!raw) return 'Lambda';
    const lower = raw.toLowerCase();
    if (lower === 'lambda' || lower === 'manual_lambda' || lower === 'pipeline_lambda_runtime') return 'Lambda';
    // Return as-is for other types (e.g., 'BrazilInterpreter')
    return raw;
  }

  /**
   * Coerce a DynamoDB numeric attribute to a number, preserving "attribute absent" as undefined.
   *
   * Booleans are rejected rather than coerced: `Number(true)` is 1, so a mistyped attribute would
   * read as a real attempt count instead of an absent one.
   */
  private static toOptionalNumber(value: unknown): number | undefined {
    if (value === undefined || value === null || value === '' || typeof value === 'boolean') return undefined;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? undefined : parsed;
  }

  /**
   * Normalize a DynamoDB job item from snake_case (Python backend) to camelCase (frontend).
   * Handles both formats so items written by either side are read correctly.
   */
  public static normalizeDynamoDBJobItem(item: any): any {
    const rawStatus = item.jobStatus || item.job_status || item.status;
    // Normalize legacy status values: completed_with_errors → failed, partial → failed
    // Normalize plural form: completed_with_alpha_deployment_failures → completed_with_alpha_deployment_failure
    const normalizedStatus =
      rawStatus === 'completed_with_errors' || rawStatus === 'partial'
        ? 'failed'
        : rawStatus === 'completed_with_alpha_deployment_failures'
        ? 'completed_with_alpha_deployment_failure'
        : rawStatus;
    return {
      jobId: item.jobId || item.job_id,
      itemType: item.itemType || item.item_type,
      pipelineId: item.pipelineId || item.pipeline_id,
      userId: item.userId || item.user_id || item.triggeredBy || item.triggered_by,
      triggeredBy: item.triggeredBy || item.triggered_by || item.userId || item.user_id || 'unknown',
      jobStatus: normalizedStatus,
      status: normalizedStatus,
      // The backend persists the live stage under `stage` (dynamodb_service.mark_stage /
      // lambda_trigger both write `stage`), NOT `currentStage`. Reading only currentStage left
      // the Job Executions stage column stuck at "Pending (1/5)" for every DynamoDB-loaded job
      // even though DynamoDB had the real stage. Fall back to `stage`/`current_stage`.
      currentStage: item.currentStage || item.current_stage || item.stage,
      targetVersions: item.targetVersions || item.target_versions || {},
      totalResources: item.totalResources ?? item.total_resources,
      completedResources: item.completedResources ?? item.completed_resources,
      failedResources: item.failedResources ?? item.failed_resources,
      // Retry/escalation state, so a reload keeps the Attempts count and the escalated indicator.
      attemptCount: this.toOptionalNumber(item.attemptCount ?? item.attempt_count),
      escalated: item.escalated === true,
      escalatedTo: item.escalatedTo ?? item.escalated_to,
      escalatedBy: item.escalatedBy ?? item.escalated_by,
      escalatedAt: item.escalatedAt ?? item.escalated_at,
      progress: item.progress ?? 0,
      progressMessage: item.progressMessage || item.progress_message || '',
      startedAt: item.startedAt || item.started_at,
      completedAt: item.completedAt || item.completed_at,
      lastUpdated: item.lastUpdated || item.last_updated,
      stages: item.stages || {},
      artifacts: item.artifacts || {},
      error: item.error || undefined,
      resourceType: this.normalizeResourceType(item.resourceType || item.resource_type),
      pipelines: item.pipelines || [],
      functions: item.functions || [],
      result: item.result,
      expiresAt: item.expiresAt || item.expires_at
    };
  }

  private static getJobRunsTableName(): string {
    return getStageConfig().tables.jobRuns;
  }

  /**
   * Create a new job run record in DynamoDB
   */
  public static async createJobRun(job: JobRun): Promise<void> {
    const config = getUpgradeMedicConfig();
    const tableName = this.getJobRunsTableName();

    const item = {
      jobId: job.jobId,
      itemType: 'JOB_METADATA',
      pipelineId: job.pipelineId || job.pipelines[0],
      userId: job.triggeredBy,
      jobStatus: job.status,
      currentStage: job.currentStage || JobStage.INITIALIZING,
      targetVersions: job.targetVersions,
      totalResources: job.totalResources || job.functions.length,
      completedResources: job.completedResources || 0,
      failedResources: job.failedResources || 0,
      progress: job.progress,
      progressMessage: job.progressMessage,
      startedAt: job.startedAt.toISOString(),
      completedAt: job.completedAt?.toISOString() || null,
      lastUpdated: new Date().toISOString(),
      stages: job.stages,
      artifacts: job.artifacts,
      error: job.error || null,
      resourceType: job.resourceType,
      pipelines: job.pipelines,
      functions: job.functions,
      // TTL: 30 days from now
      expiresAt: Math.floor(Date.now() / 1000) + 30 * 24 * 60 * 60
    };

    console.log('[UpgradeMedicDataService] Creating job run:', job.jobId);

    try {
      await DynamoDBService.putItemCrossAccount(tableName, item, config.roleArn, config.region);
      console.log('[UpgradeMedicDataService] Job run created:', job.jobId);
    } catch (error) {
      console.error('[UpgradeMedicDataService] Failed to create job run:', error);
      throw error;
    }
  }

  /**
   * Update job run stage progress
   */
  public static async updateJobStage(
    jobId: string,
    stage: JobStage,
    stageInfo: StageInfo,
    overallProgress: number,
    progressMessage: string
  ): Promise<void> {
    const config = getUpgradeMedicConfig();
    const tableName = this.getJobRunsTableName();

    console.log(`[UpgradeMedicDataService] Updating job ${jobId} stage: ${stage}`);

    try {
      await DynamoDBService.updateItemCrossAccount(
        tableName,
        { jobId, itemType: 'JOB_METADATA' },
        {
          currentStage: stage,
          progress: overallProgress,
          progressMessage,
          [`stages.${stage}`]: stageInfo,
          lastUpdated: new Date().toISOString()
        },
        config.roleArn,
        config.region
      );
    } catch (error) {
      console.error('[UpgradeMedicDataService] Failed to update job stage:', error);
      throw error;
    }
  }

  /**
   * Update job artifacts (buildLink, crLink, pipelineLink)
   */
  public static async updateJobArtifact(jobId: string, artifactType: keyof JobArtifacts, artifactUrl: string): Promise<void> {
    const config = getUpgradeMedicConfig();
    const tableName = this.getJobRunsTableName();

    console.log(`[UpgradeMedicDataService] Updating job ${jobId} artifact: ${artifactType}`);

    try {
      await DynamoDBService.updateItemCrossAccount(
        tableName,
        { jobId, itemType: 'JOB_METADATA' },
        {
          [`artifacts.${artifactType}`]: artifactUrl,
          lastUpdated: new Date().toISOString()
        },
        config.roleArn,
        config.region
      );
    } catch (error) {
      console.error('[UpgradeMedicDataService] Failed to update job artifact:', error);
      throw error;
    }
  }

  /**
   * Update job completion status
   */
  public static async completeJobRun(
    jobId: string,
    status: 'completed' | 'completed_with_alpha_deployment_failure' | 'failed' | 'cancelled',
    stages: Record<string, StageInfo>,
    artifacts: JobArtifacts,
    result?: { success: number; failed: number; total: number },
    error?: any
  ): Promise<void> {
    const config = getUpgradeMedicConfig();
    const tableName = this.getJobRunsTableName();

    console.log(`[UpgradeMedicDataService] Completing job ${jobId} with status: ${status}`);

    const updateData: any = {
      jobStatus: status,
      completedAt: new Date().toISOString(),
      lastUpdated: new Date().toISOString(),
      artifacts,
      progress: status === 'completed' || status === 'completed_with_alpha_deployment_failure' ? 100 : undefined
    };

    if (result) {
      updateData.result = result;
      updateData.completedResources = result.success;
      updateData.failedResources = result.failed;
    }

    if (error) {
      updateData.error = error;
    }

    try {
      await DynamoDBService.updateItemCrossAccount(tableName, { jobId, itemType: 'JOB_METADATA' }, updateData, config.roleArn, config.region);
      console.log(`[UpgradeMedicDataService] Job ${jobId} completed`);
    } catch (error) {
      console.error('[UpgradeMedicDataService] Failed to complete job run:', error);
      throw error;
    }
  }

  /**
   * Cancel a job run - marks it as cancelled in DynamoDB
   */
  public static async cancelJobRun(jobId: string, cancelledBy: string, currentStage?: JobStage): Promise<void> {
    const config = getUpgradeMedicConfig();
    const tableName = this.getJobRunsTableName();

    console.log(`[UpgradeMedicDataService] Cancelling job ${jobId} by user: ${cancelledBy}`);

    const updateData: any = {
      jobStatus: 'cancelled',
      completedAt: new Date().toISOString(),
      lastUpdated: new Date().toISOString(),
      error: {
        code: 'CANCELLED',
        message: 'Aborted by user',
        stage: currentStage || JobStage.INITIALIZING,
        retryable: true,
        cancelledBy
      },
      progressMessage: `Cancelled by ${cancelledBy}`
    };

    try {
      await DynamoDBService.updateItemCrossAccount(tableName, { jobId, itemType: 'JOB_METADATA' }, updateData, config.roleArn, config.region);
      console.log(`[UpgradeMedicDataService] Job ${jobId} cancelled`);
    } catch (error) {
      console.error('[UpgradeMedicDataService] Failed to cancel job run:', error);
      throw error;
    }
  }

  /**
   * Fetch job run history for a resource type.
   * Queries for both camelCase (resourceType) and snake_case (resource_type) field names
   * since the Python backend stores fields in snake_case while the frontend uses camelCase.
   */
  public static async getJobRunHistory(resourceType: string, limit?: number): Promise<any[]> {
    const config = getUpgradeMedicConfig();
    const tableName = this.getJobRunsTableName();

    try {
      // Query multiple field name formats in parallel — backend uses snake_case, frontend uses camelCase.
      // Also query by itemType to catch items where resource_type may be missing or use a different value.
      const resourceTypeLower = resourceType.toLowerCase();
      const manualVariant = `manual_${resourceTypeLower}`;
      const [camelCaseItems, snakeCaseItems, snakeCaseLowerItems, manualVariantItems, allJobMetadataItems, allJobMetadataSnakeItems] =
        await Promise.all([
          DynamoDBService.queryItemsCrossAccount(tableName, 'resourceType', resourceType, config.roleArn, config.region, limit),
          DynamoDBService.queryItemsCrossAccount(tableName, 'resource_type', resourceType, config.roleArn, config.region, limit),
          // Also query lowercase variant (e.g., 'lambda' vs 'Lambda')
          resourceTypeLower !== resourceType
            ? DynamoDBService.queryItemsCrossAccount(tableName, 'resource_type', resourceTypeLower, config.roleArn, config.region, limit)
            : Promise.resolve([]),
          // Backend stores manual jobs as 'manual_lambda' — query directly for it
          DynamoDBService.queryItemsCrossAccount(tableName, 'resource_type', manualVariant, config.roleArn, config.region, limit),
          // Catch-all: query by itemType to find jobs that may not have resource_type set
          DynamoDBService.queryItemsCrossAccount(tableName, 'itemType', 'JOB_METADATA', config.roleArn, config.region, limit),
          DynamoDBService.queryItemsCrossAccount(tableName, 'item_type', 'JOB_METADATA', config.roleArn, config.region, limit)
        ]);

      // Deduplicate by jobId
      const seen = new Set<string>();
      const allItems: any[] = [];
      for (const item of [
        ...camelCaseItems,
        ...snakeCaseItems,
        ...snakeCaseLowerItems,
        ...manualVariantItems,
        ...allJobMetadataItems,
        ...allJobMetadataSnakeItems
      ]) {
        const id = item.jobId || item.job_id;
        if (id && !seen.has(id)) {
          seen.add(id);
          const normalized = this.normalizeDynamoDBJobItem(item);
          // Include items that match the requested resource type (case-insensitive).
          // Backend may store variants like 'manual_lambda' or 'pipeline_lambda_runtime' for Lambda jobs.
          const itemResourceType = (item.resourceType || item.resource_type || '').toLowerCase();
          const normalizedResourceType = (normalized.resourceType || '').toLowerCase();
          if (itemResourceType === resourceTypeLower || itemResourceType === manualVariant || normalizedResourceType === resourceTypeLower) {
            allItems.push(normalized);
          }
        }
      }

      return allItems.sort((a: any, b: any) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime());
    } catch (error) {
      console.error('[UpgradeMedicDataService] Failed to fetch job history:', error);
      return [];
    }
  }

  /**
   * Fetch a single job run by ID
   * Used for refreshing job status/stage from DynamoDB (e.g., when polling for in-progress jobs)
   */
  public static async getJobRunById(jobId: string): Promise<JobRun | null> {
    const config = getUpgradeMedicConfig();
    const tableName = this.getJobRunsTableName();

    try {
      console.log(`[UpgradeMedicDataService] Fetching job: ${jobId}`);

      const item = await DynamoDBService.getItemCrossAccount(tableName, { jobId, itemType: 'JOB_METADATA' }, config.roleArn, config.region);

      if (!item) {
        console.log(`[UpgradeMedicDataService] Job not found: ${jobId}`);
        return null;
      }

      // Normalize snake_case fields from Python backend to camelCase
      const normalized = this.normalizeDynamoDBJobItem(item);

      const jobRun: JobRun = {
        jobId: normalized.jobId,
        pipelineId: normalized.pipelineId,
        status: normalized.jobStatus || 'failed',
        triggeredBy: normalized.triggeredBy,
        startedAt: new Date(normalized.startedAt),
        completedAt: normalized.completedAt ? new Date(normalized.completedAt) : undefined,
        lastUpdated: normalized.lastUpdated ? new Date(normalized.lastUpdated) : undefined,
        pipelines: normalized.pipelines,
        functions: normalized.functions,
        targetVersions: normalized.targetVersions,
        resourceType: normalized.resourceType,
        progress: normalized.progress,
        progressMessage: normalized.progressMessage,
        currentStage: (normalized.currentStage as JobStage) || undefined,
        stages: normalized.stages || createInitialStages(),
        artifacts: normalized.artifacts,
        error: normalized.error,
        totalResources: normalized.totalResources,
        completedResources: normalized.completedResources,
        failedResources: normalized.failedResources,
        attemptCount: normalized.attemptCount,
        escalated: normalized.escalated,
        escalatedTo: normalized.escalatedTo,
        escalatedBy: normalized.escalatedBy,
        escalatedAt: normalized.escalatedAt,
        logs: [],
        streamingText: '',
        result: normalized.result
      };

      console.log(`[UpgradeMedicDataService] Job fetched: ${jobId}, status: ${jobRun.status}, stage: ${jobRun.currentStage}`);
      return jobRun;
    } catch (error) {
      console.error('[UpgradeMedicDataService] Failed to fetch job by ID:', error);
      return null;
    }
  }

  /**
   * Fetch the auto-scheduler's usage assessments for a set of manually-managed resources.
   *
   * The scheduler writes one USAGE_ASSESSMENT item per resource on the SAME v2 job-runs table this
   * service already reads, keyed jobId=`auto-scheduler#assessment#<arn>` / itemType=`USAGE_ASSESSMENT`
   * (see DaSFintech-ResourceScannerService scheduler_state.py). The acknowledgement email is now a
   * thin nudge, so the recommendation + evidence it used to carry are read here and shown in the
   * AcknowledgeModal. Best-effort per ARN: a missing/failed read just omits that resource's context.
   */
  public static async getAssessments(resourceArns: string[]): Promise<Map<string, ResourceAssessment>> {
    const config = getUpgradeMedicConfig();
    const tableName = this.getJobRunsTableName();
    const result = new Map<string, ResourceAssessment>();

    await Promise.all(
      resourceArns.map(async (arn) => {
        try {
          const item = await DynamoDBService.getItemCrossAccount(
            tableName,
            { jobId: `${SCHEDULER_STATE_KEY_PREFIX}#assessment#${arn}`, itemType: USAGE_ASSESSMENT_ITEM_TYPE },
            config.roleArn,
            config.region
          );
          if (!item) return;
          result.set(arn, {
            recommendation: typeof item.recommendation === 'string' ? item.recommendation : '',
            evidence: Array.isArray(item.evidence) ? item.evidence.filter((line: unknown): line is string => typeof line === 'string') : [],
            inUse: item.inUse === true,
            targetVersion: item.targetVersion || item.target_version || undefined
          });
        } catch (error) {
          logger.warn(`[UpgradeMedicDataService] Could not read assessment for ${arn}: ${String(error)}`);
        }
      })
    );

    return result;
  }

  /**
   * Get summary statistics from inventory
   */
  public static getInventoryStats(accounts: Account[]) {
    let totalLambdas = 0;
    let totalPipelines = 0;
    let eolCount = 0;
    let runningCount = 0;

    // EOL runtimes list
    const eolRuntimes = ['python3.8', 'python3.9', 'nodejs14.x', 'nodejs16.x', 'java8', 'java11'];

    for (const account of accounts) {
      totalPipelines += account.pipelines.length;
      for (const pipeline of account.pipelines) {
        for (const lambda of pipeline.lambdas) {
          totalLambdas++;
          if (eolRuntimes.includes(lambda.runtime)) {
            eolCount++;
          }
          if (lambda.status === 'Running') {
            runningCount++;
          }
        }
      }
    }

    return {
      totalLambdas,
      totalPipelines,
      totalAccounts: accounts.length,
      eolCount,
      runningCount
    };
  }
}
